import { z } from 'zod';

const TimezoneApiResponseSchema = z.object({
  status: z.string(),
  timeZoneId: z.string().optional(),
});

/**
 * Resolves the IANA timezone id for a geographic location by calling the
 * Google Maps Timezone API. The `timestamp` is used so the API returns the
 * offset that applies at the event's actual start — important for dates
 * near a DST transition.
 *
 * Treats `(0, 0)` as the "missing coordinates" sentinel — matches the
 * convention used elsewhere in the codebase for events scraped without a
 * real location (Event.lat/lng are non-null with default 0 in the schema).
 *
 * On any failure (missing coordinates, network error, non-OK status,
 * malformed response) this falls back to "UTC" and logs a warning. It
 * never throws, so the caller can treat the return as a safe IANA id.
 */
export async function resolveTimezone(
  lat: number,
  lng: number,
  timestamp: Date,
  apiKey: string,
): Promise<string> {
  if (lat === 0 && lng === 0) {
    console.warn('Event missing lat/lng — falling back to UTC');
    return 'UTC';
  }

  try {
    const unixSeconds = Math.floor(timestamp.getTime() / 1000);
    const url =
      `https://maps.googleapis.com/maps/api/timezone/json` +
      `?location=${lat},${lng}` +
      `&timestamp=${unixSeconds}` +
      `&key=${encodeURIComponent(apiKey)}`;

    const response = await fetch(url, {
      signal: AbortSignal.timeout(3000),
    });

    if (!response.ok) {
      console.warn(
        `Google Maps Timezone API returned HTTP ${response.status} — falling back to UTC`,
      );
      return 'UTC';
    }

    const parsed = TimezoneApiResponseSchema.safeParse(await response.json());

    if (!parsed.success) {
      console.warn(
        'Google Maps Timezone API returned unexpected shape — falling back to UTC',
        parsed.error.message,
      );
      return 'UTC';
    }

    if (parsed.data.status !== 'OK' || !parsed.data.timeZoneId) {
      console.warn(
        `Google Maps Timezone API status=${parsed.data.status} — falling back to UTC`,
      );
      return 'UTC';
    }

    return parsed.data.timeZoneId;
  } catch (err) {
    console.warn(
      'Google Maps Timezone API call failed — falling back to UTC',
      err,
    );
    return 'UTC';
  }
}
